import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import useGetBookedSlotSchedules from '../api/useGetBookedSlotSchedules';
import useRefreshWhenStale, { refetchDaysThrough } from '../../../hooks/useRefreshWhenStale';

// One provider's days. The calendar is mounted afresh for another provider, so this one stays.
const useDailySchedules = (providerId) => {
  const [fetchedDailySchedules, setFetchedDailySchedules] = useState([]);
  const [isReloading, setIsReloading] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);
  const loadedCountRef = useRef(0);
  const isReloadingRef = useRef(false);
  const reloadBufferRef = useRef([]);
  // Bumped by every reload and refresh, so a background refresh that finishes after a reload
  // has started knows its result is the older one and drops it.
  const generationRef = useRef(0);

  const {
    data: newDailySchedules,
    loading,
    getBookedSlotSchedules,
    fetchBookedSlotSchedules,
  } = useGetBookedSlotSchedules(providerId);

  const startDate = useRef(dayjs());

  // Once, on mount. The fetcher is a new function on every render, so listing it would refetch
  // on every render.
  useEffect(() => {
    getBookedSlotSchedules(startDate.current);
    setLoadedAt(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once per arriving page. It pages on with the same fetcher, unlisted for the same reason.
  useEffect(() => {
    if (newDailySchedules.length === 0) return;

    if (isReloadingRef.current) {
      reloadBufferRef.current = [...reloadBufferRef.current, ...newDailySchedules];

      if (reloadBufferRef.current.length >= loadedCountRef.current) {
        const refreshed = reloadBufferRef.current
          .slice(0, loadedCountRef.current)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setFetchedDailySchedules(refreshed);
        reloadBufferRef.current = [];
        isReloadingRef.current = false;
        setIsReloading(false); // only now show the result
      } else {
        const lastDate = reloadBufferRef.current[reloadBufferRef.current.length - 1].date;
        getBookedSlotSchedules(dayjs(lastDate).add(1, 'day'));
      }
      return;
    }

    // Normal append
    setFetchedDailySchedules(prev => {
      const existingDates = new Set(prev.map(ds => ds.date));
      const fresh = newDailySchedules.filter(ds => !existingDates.has(ds.date));
      const merged = [...prev, ...fresh];
      loadedCountRef.current = merged.length;
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newDailySchedules]);

  const loadMoreSchedules = () => {
    // A reload pages through the same `data` a load-more would land in.
    if (isReloadingRef.current) return;

    const lastDate = fetchedDailySchedules[fetchedDailySchedules.length - 1]?.date;
    if (!lastDate) return;
    getBookedSlotSchedules(dayjs(lastDate).add(1, 'day'));
  };

  // Always from today: a dashboard left open past midnight would otherwise ask for yesterday,
  // which the backend rejects.
  const reloadSchedules = () => {
    generationRef.current += 1;
    startDate.current = dayjs();
    isReloadingRef.current = true;
    reloadBufferRef.current = [];
    setIsReloading(true); // block UI until all pages are done
    setLoadedAt(Date.now());
    getBookedSlotSchedules(startDate.current);
  };

  /**
   * The background counterpart of `reloadSchedules`, run when the grid goes stale: the same
   * number of days from today, fetched off to the side and swapped in whole. Nothing blanks out
   * meanwhile, so the board keeps its scroll position and an open modal is left alone.
   */
  const refreshSchedules = async () => {
    if (isReloadingRef.current) return;

    // Nothing on screen yet: the first load failed. Try it again as a first load.
    if (loadedCountRef.current === 0) {
      startDate.current = dayjs();
      setLoadedAt(Date.now());
      getBookedSlotSchedules(startDate.current);
      return;
    }

    const generation = ++generationRef.current;
    const throughDate = dayjs().add(loadedCountRef.current - 1, 'day').format('YYYY-MM-DD');
    setLoadedAt(Date.now());

    const days = await refetchDaysThrough(fetchBookedSlotSchedules, throughDate);
    if (!days || generation !== generationRef.current) return;

    startDate.current = dayjs();
    setFetchedDailySchedules((prev) => {
      // Days paged out to while the refresh was in flight are kept.
      const merged = [...days, ...prev.filter((day) => day.date > throughDate)];
      loadedCountRef.current = merged.length;
      return merged;
    });
  };

  useRefreshWhenStale(refreshSchedules, loadedAt);

  return {
    fetchedDailySchedules,
    loading: loading || isReloading, // expose combined loading state
    loadMoreSchedules,
    reloadSchedules,
  };
};

export default useDailySchedules;
